import React, { useEffect, useState } from 'react';

import { useEditor } from 'components/Canvas';
import { getVarKeys } from './utils';
import { isObjectURL, remoteImageToFile } from 'helpers/editor';
import { previewFailureReason } from 'helpers/stacks';
import Icons from '../Icons';
import VarsForm from './VarsForm';
import Preview from './Preview';
import Loading from 'components/Loading/Loading';
import PreviewDoubleSided from './PreviewDoubleSided';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  Flex,
  Box,
  IconButton,
  Button,
} from '@chakra-ui/react';

import useTemplateEditorContext from 'scenes/templateEditor/hooks/useTemplateEditorContext';
import { useGetTemplatePreviewMutation } from 'modules/templates/templatesApi';

const STEPS = {
  NOT_STARTED: 'NOT_STARTED',
  STARTED: 'STARTED',
  HAS_VARS: 'HAS_VARS',
  NO_VARS: 'NO_VARS',
  PREVIEW_LOADING: 'PREVIEW_LOADING',
  PREVIEW: 'PREVIEW',
  PREVIEW_DOUBLE_SIDED: 'PREVIEW_DOUBLE_SIDED',
  ERROR: 'ERROR',
};

function PreviewModal({ isOpen, onClose }) {
  const editor = useEditor();

  const [options, setOptions] = useState({
    step: STEPS.NOT_STARTED,
    varKeys: [],
    previewImage: null,
    previewImages: [],
  });

  const { doubleSided } = useTemplateEditorContext();
  const [getTemplatePreview] = useGetTemplatePreviewMutation();

  useEffect(() => {
    if (editor) {
      const template = editor.exportToJSON();
      const varKeys = getVarKeys(template);
      setOptions({
        step: STEPS.STARTED,
        varKeys,
      });
      if (varKeys.length) {
        setOptions({
          step: STEPS.HAS_VARS,
          varKeys,
        });
      } else {
        setOptions({
          step: STEPS.NO_VARS,
          varKeys,
        });
      }
    }
  }, [editor]);

  useEffect(() => {
    if (options.step === STEPS.NO_VARS) {
      buildPreview({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const downloadImage = () => {
    if (options.previewImage) {
      const a = document.createElement('a');
      a.target = '_blank';
      a.href = options.previewImage;
      a.download = 'preview_template.png';
      a.click();
    }
    return false;
  };

  const buildPreview = async params => {
    setOptions(prev => ({ ...prev, step: STEPS.PREVIEW_LOADING }));

    const exportedTemplate = editor.exportToJSON();
    const payload = {
      template: { ...exportedTemplate, name: 'Preview' },
      params,
    };

    const getImageFileFromUrl = async (url, fileName) => {
      const blob = await fetch(url).then(res => res.blob());
      const extension = blob.type.split('/')[1] || 'jpg';
      const safeFileName = fileName || `image.${extension}`;
      return new File([blob], safeFileName, { type: blob.type });
    };

    if (!doubleSided) {
      const url = exportedTemplate.bg_img?.original_url;

      if (url && isObjectURL(url)) {
        const fileName = editor.handlers.frameHandler.backgroundFileName;
        payload.bg_img = await getImageFileFromUrl(url, fileName);
      }

      try {
        const { img_base64 } = await getTemplatePreview(payload).unwrap();
        setOptions(prev => ({
          ...prev,
          step: STEPS.PREVIEW,
          previewImage: `data:image/jpg;base64,${img_base64}`,
        }));
      } catch (error) {
        setOptions(prev => ({
          ...prev,
          step: STEPS.ERROR,
          // a push-down stack that does not fit is the designer's to fix, so
          // the stitcher's reason is shown rather than the generic message
          failureReason: previewFailureReason(error),
        }));
      }

      return;
    }

    // double-sided logic
    const imagePages = [];
    let failureReason = null;
    const adTemplatePages = payload.template.ad_template_pages;

    for (const page of adTemplatePages) {
      const url = page.bg_img?.original_url;
      const fileName = page.bg_img?.fileName;

      // set canvas objects and get the metadata
      payload.template.ad_template_vars = page.ad_template_vars;
      payload.template.metadata = page.metadata;

      // set the bg_img in case exist
      if (url && isObjectURL(url)) {
        payload.bg_img = await getImageFileFromUrl(url, fileName);
        payload.template.bg_img = page.bg_img;
      } else if (url) {
        payload.bg_img = await remoteImageToFile(url);
        payload.template.bg_img = page.bg_img;
      }

      try {
        const { img_base64 } = await getTemplatePreview(payload).unwrap();
        imagePages.push(`data:image/jpg;base64,${img_base64}`);
      } catch (error) {
        // Skip failed pages, keeping the first reason the stitcher gave
        failureReason = failureReason || previewFailureReason(error);
      }

      // Clean fields for next iteration
      delete payload.bg_img;
      payload.template.bg_img = null;
    }

    // update status modal
    setOptions(prev => ({
      ...prev,
      step: imagePages.length > 0 ? STEPS.PREVIEW_DOUBLE_SIDED : STEPS.ERROR,
      previewImages: imagePages.length > 0 ? imagePages : undefined,
      failureReason,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay sx={{ overflow: 'hidden' }} />
      <ModalContent
        sx={{
          maxWidth: 'max-content',
          minWidth: '480px',
          paddingBottom: '1.5rem',
        }}
      >
        <Flex
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.5rem 1.5rem 0',
          }}
        >
          <Box fontWeight="600">Template preview</Box>
          <Box>
            {options.step === STEPS.PREVIEW && (
              <IconButton
                onClick={downloadImage}
                color="#000000"
                borderRadius="0"
                variant="ghost"
                icon={<Icons.Download size={20} />}
              />
            )}
            <IconButton
              color="#000000"
              borderRadius="0"
              variant="ghost"
              icon={<Icons.Cross size={20} />}
              onClick={onClose}
            />
          </Box>
        </Flex>
        <Box>
          {
            {
              [STEPS.NOT_STARTED]: <div>NOT STARTED</div>,
              [STEPS.STARTED]: <div>STARTED</div>,
              [STEPS.PREVIEW_LOADING]: (
                <Flex justifyContent="center" padding="2rem">
                  <Loading size="lg" />
                </Flex>
              ),
              [STEPS.HAS_VARS]: (
                <VarsForm
                  varKeys={options.varKeys}
                  buildTemplate={buildPreview}
                />
              ),
              [STEPS.PREVIEW]: <Preview previewImage={options.previewImage} />,
              [STEPS.PREVIEW_DOUBLE_SIDED]: (
                <PreviewDoubleSided previewImages={options.previewImages} />
              ),
              [STEPS.ERROR]: (
                <>
                  <Flex
                    padding="1.5rem"
                    textAlign="center"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="14px"
                  >
                    An error occurred trying to generate the preview. Please try
                    again.
                  </Flex>
                  {options.failureReason && (
                    <Flex
                      padding="0 1.5rem 1.5rem"
                      textAlign="center"
                      justifyContent="center"
                      fontSize="13px"
                      color="#e53e3e"
                      maxWidth="480px"
                      name="previewFailureReason"
                    >
                      {options.failureReason}
                    </Flex>
                  )}
                  <Box display="flex" justifyContent="center">
                    <Button
                      onClick={onClose}
                      size="sm"
                      colorScheme="blackAlpha"
                      width={100}
                    >
                      OK
                    </Button>
                  </Box>
                </>
              ),
            }[options.step]
          }
        </Box>
      </ModalContent>
    </Modal>
  );
}

export default PreviewModal;
